// src/pages/Privacy.tsx
import { Link } from "react-router-dom";

export default function Privacy() {
  const updated = "9 November 2025"; // keep this current

  return (
    <>
      <title>Privacy Policy | Beaumonts Plumbing &amp; Heating</title>
      <meta
        name="description"
        content="Privacy Policy for Beaumonts Plumbing & Heating. How we collect, use, store and share your personal data, and your rights under UK GDPR."
      />

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14 md:py-20">
        <div className="max-w-3xl">
          <p className="text-xs font-medium tracking-widest uppercase text-[#3bb3ef]">Privacy</p>
          <h1 className="mt-2 text-3xl md:text-5xl font-semibold tracking-[-0.02em]">
            Privacy Policy
          </h1>
          <p className="mt-3 text-[15px] md:text-base text-neutral-700">
            We only use your details to run our business, deliver work you ask us to do, and keep records we legally need to keep. No spam. No selling your data.
          </p>
          <p className="mt-2 text-sm text-neutral-600">Last updated: {updated}</p>
        </div>

        {/* Who we are */}
        <div className="mt-10 space-y-6 text-[15px] md:text-base text-neutral-800 leading-relaxed">
          <section id="who">
            <h2 className="text-xl md:text-2xl font-semibold">Who we are</h2>
            <p className="mt-2">
              Beaumonts Plumbing &amp; Heating (“we”, “us”) provides plumbing and heating services in Lincoln and nearby areas.
            </p>
            <ul className="mt-2 text-neutral-700">
              <li>Trading name: <strong>Beaumonts Plumbing &amp; Heating</strong></li>
              <li>Location: Lincoln, Lincolnshire, UK</li>
              <li>Phone: <a className="underline underline-offset-4 decoration-[#3bb3ef]" href="tel:+441522692592">01522 692592</a></li>
              <li>Mobile: <a className="underline underline-offset-4 decoration-[#3bb3ef]" href="tel:+447803608738">07803 608738</a></li>
              <li>Email: <a className="underline underline-offset-4 decoration-[#3bb3ef]" href="mailto:ian@beaumontsplumbing.co.uk">ian@beaumontsplumbing.co.uk</a></li>
            </ul>
          </section>

          {/* How we collect data */}
          <section id="collect">
            <h2 className="text-xl md:text-2xl font-semibold">How we collect your data</h2>
            <ul className="mt-2 list-disc pl-5 text-neutral-700">
              <li>When you send our contact form on this website.</li>
              <li>When you call, text or email us.</li>
              <li>When we carry out a survey, visit your property, or deliver work.</li>
              <li>When you agree a quote or pay an invoice.</li>
              <li>Basic analytics about site usage to help us keep the site running well.</li>
            </ul>
          </section>

          {/* What we collect */}
          <section id="what">
            <h2 className="text-xl md:text-2xl font-semibold">What we collect</h2>
            <ul className="mt-2 list-disc pl-5 text-neutral-700">
              <li>Contact details: name, email, phone number.</li>
              <li>Job details: address, photos you share with us, notes about the job, access info.</li>
              <li>Work records: quotes, invoices, certificates, warranty and servicing history.</li>
              <li>Technical data: IP address, device and browser basics, pages viewed. This is aggregated for performance and security.</li>
              <li>Payment info: we keep invoice records and amounts paid. We do not store card details on our site.</li>
            </ul>
          </section>

          {/* Why and legal bases */}
          <section id="why">
            <h2 className="text-xl md:text-2xl font-semibold">Why we use your data</h2>
            <p className="mt-2">We use your data for the following reasons under UK GDPR:</p>
            <ul className="mt-2 list-disc pl-5 text-neutral-700">
              <li><strong>To respond to enquiries and provide quotes</strong> — legitimate interests and pre-contract steps.</li>
              <li><strong>To deliver agreed work and manage your job</strong> — contract.</li>
              <li><strong>To issue invoices, keep accounts and records</strong> — legal obligation.</li>
              <li><strong>To provide certificates, warranties and aftercare</strong> — contract and legitimate interests.</li>
              <li><strong>To secure and improve the website</strong> — legitimate interests.</li>
              <li><strong>To meet legal and regulatory requirements</strong> — legal obligation.</li>
            </ul>
          </section>

          {/* Sharing */}
          <section id="share">
            <h2 className="text-xl md:text-2xl font-semibold">Who we share it with</h2>
            <p className="mt-2">We only share what’s necessary with trusted providers to run the business:</p>
            <ul className="mt-2 list-disc pl-5 text-neutral-700">
              <li><strong>Website hosting and email delivery</strong>: our site is hosted on reputable cloud infrastructure. Emails are handled through our mail provider so we can read and reply to enquiries.</li>
              <li><strong>Analytics and performance</strong>: privacy-minded analytics to understand what content is useful and keep the site fast and secure.</li>
              <li><strong>Payment and accounting</strong>: invoicing and bookkeeping tools for quotes, invoices and tax records.</li>
              <li><strong>Regulators and insurers</strong>: only if required by law or to handle a claim.</li>
            </ul>
            <p className="mt-2 text-neutral-700">
              Some providers may process data outside the UK. Where that happens, we use appropriate safeguards such as standard contractual clauses.
            </p>
          </section>

          {/* Cookies */}
          <section id="cookies">
            <h2 className="text-xl md:text-2xl font-semibold">Cookies and analytics</h2>
            <p className="mt-2">
              Our site uses essential cookies for things like security and forms. We may also use simple, privacy-friendly analytics to see which pages are useful. You can manage non-essential cookies using the banner on this site.
            </p>
            <p className="mt-2 text-sm text-neutral-600">
              You can change your choice any time via the cookie banner. Blocking non-essential cookies will not stop the site from working.
            </p>
          </section>

          {/* Retention */}
          <section id="retention">
            <h2 className="text-xl md:text-2xl font-semibold">How long we keep it</h2>
            <ul className="mt-2 list-disc pl-5 text-neutral-700">
              <li>Enquiry emails and contact form messages: up to <strong>24 months</strong>.</li>
              <li>Job, invoice and tax records: normally <strong>6 to 7 years</strong>.</li>
              <li>Certificates and warranty records: for the life of the product or as required by the issuer.</li>
              <li>Website logs and analytics: typically <strong>up to 12 months</strong> in aggregate form.</li>
            </ul>
          </section>

          {/* Your rights */}
          <section id="rights">
            <h2 className="text-xl md:text-2xl font-semibold">Your rights</h2>
            <p className="mt-2">
              Under UK data protection law you can ask us to access, correct, erase or restrict your data, or to object to certain uses. You can also ask for a copy of your data to move it to another provider.
            </p>
            <p className="mt-2">
              To use your rights, email <a className="underline underline-offset-4 decoration-[#3bb3ef]" href="mailto:ian@beaumontsplumbing.co.uk">ian@beaumontsplumbing.co.uk</a> with your request. We might need to confirm your identity before we act.
            </p>
            <p className="mt-2 text-neutral-700">
              If you’re unhappy with how we handle your data, you can complain to the UK Information Commissioner’s Office (ICO) at <a className="underline underline-offset-4 decoration-[#3bb3ef]" href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.
            </p>
          </section>

          {/* Children */}
          <section id="children">
            <h2 className="text-xl md:text-2xl font-semibold">Children’s data</h2>
            <p className="mt-2">
              Our services are for adults. We do not knowingly collect children’s personal data.
            </p>
          </section>

          {/* Security */}
          <section id="security">
            <h2 className="text-xl md:text-2xl font-semibold">Security</h2>
            <p className="mt-2">
              We take reasonable steps to protect your data: encrypted connections (HTTPS), access controls, and limiting who can see what. No system is perfect, but we work to keep your information safe.
            </p>
          </section>

          {/* Changes */}
          <section id="changes">
            <h2 className="text-xl md:text-2xl font-semibold">Changes to this policy</h2>
            <p className="mt-2">
              We may update this page to reflect how we work or changes in the law. We post the latest version here and update the date at the top.
            </p>
          </section>

          {/* Contact */}
          <section id="contact" className="border border-neutral-200 rounded-2xl p-5 md:p-6 bg-white shadow-[0_1px_0_0_rgba(0,0,0,0.03)]">
            <h2 className="text-xl md:text-2xl font-semibold">Questions or requests</h2>
            <p className="mt-2">
              Email <a className="underline underline-offset-4 decoration-[#3bb3ef]" href="mailto:ian@beaumontsplumbing.co.uk">ian@beaumontsplumbing.co.uk</a> or call{" "}
              <a className="underline underline-offset-4 decoration-[#3bb3ef]" href="tel:+441522692592">01522 692592</a>. We’ll get back to you promptly.
            </p>
          </section>

          {/* CTA back to site */}
          <div className="mt-8">
            <Link
              to="/contact"
              className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-5 py-3 text-sm font-medium hover:brightness-110"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
